import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, Search, Star, Clock, TrendingUp, Sparkles, 
  GraduationCap, BookMarked, Lightbulb, ChevronRight,
  Filter, Grid, List, Zap, Loader2, Wand2, X, Save, Library, Trash2,
  ArrowLeft, Home, Compass, Heart, Landmark, Smile, Eye, Leaf, CheckCircle2,
  BookText, ChevronDown, ChevronUp, LogOut
} from 'lucide-react';import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { getStageFromYear } from '../config/taxonomy';

const API = process.env.REACT_APP_BACKEND_URL;

// Accordion component for saved passages - compact view with expand/collapse
const SavedPassagesAccordion = ({ passages, onRead, onDelete }) => {
  const [expandedId, setExpandedId] = useState(null);
  
  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="space-y-2 py-4">
      {passages.map((passage, index) => {
        const isExpanded = expandedId === passage.id;
        
        return (
          <div 
            key={passage.id}
            className={`rounded-lg border transition-all duration-300 ${
              isExpanded 
                ? 'bg-white/70 border-violet-500/40' 
                : 'bg-white/30 border-gray-200 hover:border-gray-300'
            }`}
          >
            {/* Collapsed Header - Always visible */}
            <div 
              className="flex items-center gap-3 p-3 cursor-pointer"
              onClick={() => toggleExpand(passage.id)}
            >
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-violet-500/20 text-violet-400 text-xs font-bold flex-shrink-0">
                {index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-gray-900 font-medium text-sm truncate">
                    {passage.title?.replace('AI Generated: ', '')}
                  </span>
                  <Badge className="bg-slate-700/50 text-gray-600 text-[10px] px-1.5">{passage.type}</Badge>
                </div>
                {!isExpanded && (
                  <p className="text-gray-500 text-xs truncate">{passage.word_count} words • Read {passage.times_read || 0}x</p>
                )}
              </div>
              <ChevronRight className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
            </div>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="px-3 pb-3 space-y-3 animate-in slide-in-from-top-2 duration-200">
                {/* Passage Preview */}
                <div className="bg-gray-100/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                    {passage.excerpt}
                  </p>
                </div>

                {/* Stats & Questions */}
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {passage.reading_time}
                  </span>
                  <span className="flex items-center gap-1">
                    <BookOpen className="w-3 h-3" />
                    {passage.word_count} words
                  </span>
                  {passage.comprehension_questions?.length > 0 && (
                    <span className="flex items-center gap-1 text-amber-400">
                      <GraduationCap className="w-3 h-3" />
                      {passage.comprehension_questions.length} questions
                    </span>
                  )}
                  {passage.quiz_scores?.length > 0 && (
                    <span className="text-emerald-400">
                      Best: {Math.max(...passage.quiz_scores.map(s => s.score))}%
                    </span>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); onRead(passage); }}
                    className="flex-1 bg-violet-600 hover:bg-violet-500 text-gray-900 text-xs h-8"
                  >
                    <BookOpen className="w-3 h-3 mr-1" />
                    Read with AI Tools
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); onDelete(passage.id); }}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 px-2"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); toggleExpand(passage.id); }}
                    className="text-gray-600 hover:text-gray-900 h-8 px-2"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const ELibrary = ({ progress }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [passages, setPassages] = useState([]);
  const [wordOfDay, setWordOfDay] = useState(null);
  const [vocabulary, setVocabulary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  const [wordOfDayMeta, setWordOfDayMeta] = useState({ ai_generated: false, level: '11plus' });
  
  // Get user's year group and determine available levels
  const userYearGroup = progress?.user?.studentProfile?.yearGroup || 
                        progress?.user?.yearGroup || 
                        null;

  // Check if user is admin or superuser - they get full access
  const isAdminOrSuper = useMemo(() => {
    const user = progress?.user;
    if (!user) return false;
    return user?.userType === 'admin' || 
           user?.userType === 'superuser' || 
           user?.isAdmin === true ||
           user?.isSuperUser === true ||
           user?.email?.includes('admin@');
  }, [progress?.user]);

  // Map year group string to allowed level
  const getAllowedLevel = useMemo(() => {
    if (isAdminOrSuper || !userYearGroup) return null; // null means all levels
    
    const yearLower = userYearGroup.toLowerCase().trim();
    
    // Direct mapping from year group names to level IDs
    const yearToLevel = {
      'year 1': '7plus',
      'year 2': '7plus',
      'year-1': '7plus',
      'year-2': '7plus',
      'year 3': '11plus',
      'year 4': '11plus',
      'year 5': '11plus',
      'year 6': '11plus',
      'year-3': '11plus',
      'year-4': '11plus',
      'year-5': '11plus',
      'year-6': '11plus',
      '7+': '7plus',
      '7plus': '7plus',
      '11+': '11plus',
      '11plus': '11plus',
      'year 7': 'yr9',
      'year 8': 'yr9',
      'year 9': 'yr9',
      'year-7': 'yr9',
      'year-8': 'yr9',
      'year-9': 'yr9',
      'year 10': 'gcse',
      'year 11': 'gcse',
      'year-10': 'gcse',
      'year-11': 'gcse',
      'gcse': 'gcse'
    };
    
    return yearToLevel[yearLower] || null;
  }, [userYearGroup, isAdminOrSuper]);
  
  // All available levels
  const allLevels = [
    { id: '7plus', label: '7+', color: 'bg-pink-600', stages: ['ks1', '7-plus'] },
    { id: '11plus', label: '11+', color: 'bg-violet-600', stages: ['ks2-upper', '11-plus', 'ks2-lower'] },
    { id: 'yr9', label: 'Yr 9', color: 'bg-blue-600', stages: ['ks3'] },
    { id: 'gcse', label: 'GCSE', color: 'bg-emerald-600', stages: ['ks4'] }
  ];
  
  // Filter levels based on user's year group (admin gets all)
  const availableLevels = useMemo(() => {
    if (!getAllowedLevel) {
      return allLevels; // Admin or no restriction
    }
    
    return allLevels.filter(level => level.id === getAllowedLevel);
  }, [getAllowedLevel]);
  
  // Set default selected level based on user's allowed level
  const [selectedLevel, setSelectedLevel] = useState(() => {
    if (!userYearGroup) return '11plus';
    
    const yearLower = userYearGroup.toLowerCase().trim();
    const yearToLevel = {
      'year 1': '7plus', 'year 2': '7plus', 'year-1': '7plus', 'year-2': '7plus',
      'year 3': '11plus', 'year 4': '11plus', 'year 5': '11plus', 'year 6': '11plus',
      'year-3': '11plus', 'year-4': '11plus', 'year-5': '11plus', 'year-6': '11plus',
      '7+': '7plus', '7plus': '7plus', '11+': '11plus', '11plus': '11plus',
      'year 7': 'yr9', 'year 8': 'yr9', 'year 9': 'yr9',
      'year-7': 'yr9', 'year-8': 'yr9', 'year-9': 'yr9',
      'year 10': 'gcse', 'year 11': 'gcse', 'year-10': 'gcse', 'year-11': 'gcse',
      'gcse': 'gcse'
    };
    return yearToLevel[yearLower] || '11plus';
  });
  
  // AI Passage Generator state
  const [showPassageGenerator, setShowPassageGenerator] = useState(false);
  const [generatingPassage, setGeneratingPassage] = useState(false);
  const [generatedPassage, setGeneratedPassage] = useState(null);
  const [generatorForm, setGeneratorForm] = useState({
    topic: '',
    type: 'fiction',
    wordCount: 200
  });
  // LLM Models (randomly selected)
  const LLM_MODELS = [
    { id: 'openai', name: 'GPT-4o', icon: '🤖' },
    { id: 'anthropic', name: 'Claude Sonnet', icon: '🧠' },
    { id: 'gemini', name: 'Gemini Flash', icon: '⚡' }
  ];
  
  const getRandomLLM = () => LLM_MODELS[Math.floor(Math.random() * LLM_MODELS.length)].id;
  
  // My Saved Passages state
  const [savedPassages, setSavedPassages] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [savingPassage, setSavingPassage] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState('browse');
  
  // 11+ Themed Passages state
  const [elevenPlusThemes, setElevenPlusThemes] = useState([]);
  const [nonfictionThemes, setNonfictionThemes] = useState([]);
  const [readingHistory, setReadingHistory] = useState({});
  const [loadingThemes, setLoadingThemes] = useState(false);
  const [expandedThemes, setExpandedThemes] = useState({});
  
  // Theme icons mapping (fiction + non-fiction)
  const themeIcons = {
    // Fiction
    adventure: Compass, family: Heart, fantasy: Sparkles,
    historical: Landmark, humour: Smile, mystery: Eye,
    nature: Leaf, school: GraduationCap,
    // Non-fiction
    arctic: Compass, disasters: Landmark, inventions: Sparkles,
    space: Star, figures: BookText, endangered: Leaf,
    conservation: Leaf
  };

  // Theme colour mapping (fiction + non-fiction)
  const themeColors = {
    // Fiction
    adventure: { bg: 'from-amber-600/15 to-orange-600/10', border: 'border-amber-500/25', icon: 'text-amber-400', badge: 'bg-amber-500/20 text-amber-300' },
    family: { bg: 'from-rose-600/15 to-pink-600/10', border: 'border-rose-500/25', icon: 'text-rose-400', badge: 'bg-rose-500/20 text-rose-300' },
    fantasy: { bg: 'from-violet-600/15 to-purple-600/10', border: 'border-violet-500/25', icon: 'text-violet-400', badge: 'bg-violet-500/20 text-violet-300' },
    historical: { bg: 'from-stone-600/15 to-amber-900/10', border: 'border-stone-500/25', icon: 'text-stone-400', badge: 'bg-stone-500/20 text-stone-300' },
    humour: { bg: 'from-yellow-600/15 to-lime-600/10', border: 'border-yellow-500/25', icon: 'text-yellow-400', badge: 'bg-yellow-500/20 text-yellow-300' },
    mystery: { bg: 'from-indigo-600/15 to-blue-600/10', border: 'border-indigo-500/25', icon: 'text-indigo-400', badge: 'bg-indigo-500/20 text-indigo-300' },
    nature: { bg: 'from-emerald-600/15 to-teal-600/10', border: 'border-emerald-500/25', icon: 'text-emerald-400', badge: 'bg-emerald-500/20 text-emerald-300' },
    school: { bg: 'from-blue-600/15 to-cyan-600/10', border: 'border-blue-500/25', icon: 'text-blue-400', badge: 'bg-blue-500/20 text-blue-300' },
    // Non-fiction
    arctic: { bg: 'from-sky-600/15 to-cyan-600/10', border: 'border-sky-500/25', icon: 'text-sky-400', badge: 'bg-sky-500/20 text-sky-300' },
    disasters: { bg: 'from-red-600/15 to-orange-600/10', border: 'border-red-500/25', icon: 'text-red-400', badge: 'bg-red-500/20 text-red-300' },
    inventions: { bg: 'from-amber-600/15 to-yellow-600/10', border: 'border-amber-500/25', icon: 'text-amber-400', badge: 'bg-amber-500/20 text-amber-300' },
    space: { bg: 'from-slate-600/15 to-indigo-600/10', border: 'border-slate-500/25', icon: 'text-gray-700', badge: 'bg-slate-500/20 text-gray-700' },
    figures: { bg: 'from-purple-600/15 to-violet-600/10', border: 'border-purple-500/25', icon: 'text-purple-400', badge: 'bg-purple-500/20 text-purple-300' },
    endangered: { bg: 'from-orange-600/15 to-red-600/10', border: 'border-orange-500/25', icon: 'text-orange-400', badge: 'bg-orange-500/20 text-orange-300' },
    conservation: { bg: 'from-teal-600/15 to-green-600/10', border: 'border-teal-500/25', icon: 'text-teal-400', badge: 'bg-teal-500/20 text-teal-300' }
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchContent();
    fetchWordOfDay();
    fetchVocabulary();
    if (user.id) {
      fetchSavedPassages();
    }
    if (selectedLevel === '11plus') {
      fetchElevenPlusThemes();
      const token = localStorage.getItem('token');
      if (token) fetchReadingHistory(token);
    }
  }, [selectedLevel, selectedType]);

  const fetchElevenPlusThemes = async () => {
    setLoadingThemes(true);
    try {
      const response = await axios.get(`${API}/api/elibrary/themes`);
      const sortedFiction = (response.data.themes || []).sort((a, b) => a.name.localeCompare(b.name));
      const sortedNF = (response.data.nonfiction_themes || []).sort((a, b) => a.name.localeCompare(b.name));
      setElevenPlusThemes(sortedFiction);
      setNonfictionThemes(sortedNF);
      // Expand all themes by default
      const expanded = {};
      [...sortedFiction, ...sortedNF].forEach(t => { expanded[t.key] = true; });
      setExpandedThemes(expanded);
    } catch (error) {
      console.error('Error fetching 11+ themes:', error);
    } finally {
      setLoadingThemes(false);
    }
  };

  const fetchReadingHistory = async (token) => {
    try {
      const response = await axios.get(`${API}/api/elibrary/passages/reading-history/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const history = {};
      (response.data.readings || []).forEach(r => { history[r.passage_id] = r; });
      setReadingHistory(history);
    } catch (error) {
      console.error('Error fetching reading history:', error);
    }
  };

  const toggleTheme = (key) => {
    setExpandedThemes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const fetchContent = async () => {
    try {
      setLoading(true);
      const typeParam = selectedType === 'all' ? '' : `&type=${selectedType}`;
      const response = await axios.get(`${API}/api/elibrary/passages?level=${selectedLevel}${typeParam}`);
      setPassages(response.data.passages);
    } catch (error) {
      console.error('Error fetching passages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWordOfDay = async () => {
    try {
      // Check localStorage cache first (stays static for 24 hours)
      const cacheKey = `wordOfDay_${selectedLevel}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const hoursPassed = (Date.now() - timestamp) / (1000 * 60 * 60);
        // If less than 24 hours old, use cached data
        if (hoursPassed < 24) {
          setWordOfDay(data.word);
          setWordOfDayMeta({
            ai_generated: data.ai_generated,
            level: data.level,
            theme: data.theme
          });
          return;
        }
      }
      
      // Fetch fresh data if no cache or cache expired
      const response = await axios.get(`${API}/api/elibrary/word-of-the-day?level=${selectedLevel}&use_ai=true`);
      setWordOfDay(response.data.word);
      setWordOfDayMeta({
        ai_generated: response.data.ai_generated,
        level: response.data.level,
        theme: response.data.theme
      });
      
      // Cache the response with timestamp
      localStorage.setItem(cacheKey, JSON.stringify({
        data: response.data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Error fetching word of day:', error);
    }
  };

  const fetchVocabulary = async () => {
    try {
      const response = await axios.get(`${API}/api/elibrary/vocabulary?level=${selectedLevel}&category=core_vocabulary`);
      setVocabulary(response.data.vocabulary?.slice(0, 6) || []);
    } catch (error) {
      console.error('Error fetching vocabulary:', error);
    }
  };

  const generatePassage = async () => {
    if (!generatorForm.topic.trim()) return;
    
    setGeneratingPassage(true);
    try {
      const randomLLM = getRandomLLM();
      const response = await axios.post(`${API}/api/elibrary/ai/generate-passage`, {
        topic: generatorForm.topic,
        level: selectedLevel,
        type: generatorForm.type,
        word_count: generatorForm.wordCount,
        llm_provider: randomLLM
      });
      setGeneratedPassage({...response.data.passage, topic: generatorForm.topic, type: generatorForm.type});
    } catch (error) {
      console.error('Error generating passage:', error);
      alert('Failed to generate passage. Please try again.');
    } finally {
      setGeneratingPassage(false);
    }
  };

  const fetchSavedPassages = async () => {
    if (!user.id) return;
    setLoadingSaved(true);
    try {
      const response = await axios.get(`${API}/api/elibrary/saved-passages?user_id=${user.id}`);
      setSavedPassages(response.data.passages || []);
    } catch (error) {
      console.error('Error fetching saved passages:', error);
    } finally {
      setLoadingSaved(false);
    }
  };

  const savePassageToLibrary = async () => {
    if (!generatedPassage || !user.id) return;
    setSavingPassage(true);
    try {
      await axios.post(`${API}/api/elibrary/saved-passages?user_id=${user.id}`, {
        title: generatedPassage.title,
        author: generatedPassage.author || 'AI Assistant',
        excerpt: generatedPassage.excerpt,
        word_count: generatedPassage.word_count,
        reading_time: generatedPassage.reading_time,
        difficulty: generatedPassage.difficulty || 'medium',
        themes: generatedPassage.themes || [generatorForm.topic],
        vocabulary_highlights: generatedPassage.vocabulary_highlights || [],
        comprehension_questions: generatedPassage.comprehension_questions || [],
        type: generatorForm.type,
        topic: generatorForm.topic
      });
      alert('Passage saved to your library!');
      fetchSavedPassages();
    } catch (error) {
      console.error('Error saving passage:', error);
      alert('Failed to save passage. Please try again.');
    } finally {
      setSavingPassage(false);
    }
  };

  const deleteSavedPassage = async (passageId) => {
    if (!user.id || !window.confirm('Delete this passage from your library?')) return;
    try {
      await axios.delete(`${API}/api/elibrary/saved-passages/${passageId}?user_id=${user.id}`);
      setSavedPassages(prev => prev.filter(p => p.id !== passageId));
    } catch (error) {
      console.error('Error deleting passage:', error);
      alert('Failed to delete passage.');
    }
  };

  const openSavedPassage = (passage) => {
    sessionStorage.setItem('ai_generated_passage', JSON.stringify(passage));
    navigate('/elibrary/read/ai-generated');
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      fetchContent();
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/elibrary/search?query=${encodeURIComponent(searchQuery)}&level=${selectedLevel}`);
      setPassages(response.data.results.passages);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const difficultyColors = {
    easy: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    medium: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    hard: 'bg-rose-500/20 text-rose-400 border-rose-500/30'
  };

  const typeIcons = {
    fiction: '📖',
    non_fiction: '📚',
    poetry: '🎭'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-gray-900" />
              </div>
              <div>
                <h1 className="text-base sm:text-xl font-bold text-gray-900 leading-none">Ed's Library</h1>
                <p className="text-xs text-gray-500 hidden sm:block">Reading & Vocabulary</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(-1)}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 sm:px-3"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">Back</span>
              </Button>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => navigate('/welcome')}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 sm:px-3"
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">Home</span>
              </Button>
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => {
                  localStorage.clear();
                  window.location.replace('/');
                }}
                className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-2 sm:px-3"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* Hero Section with Word of the Day */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-violet-600/20 via-indigo-600/20 to-purple-600/20 border border-gray-200 p-4 sm:p-6 md:p-8">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOGM5Ljk0MSAwIDE4LTguMDU5IDE4LTE4cy04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAyIi8+PC9nPjwvc3ZnPg==')] opacity-50" />
            
            <div className="relative grid md:grid-cols-2 gap-6 items-center">
              <div>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                  <span className="text-amber-400 text-sm font-medium">Word of the Day</span>
                  {wordOfDayMeta.ai_generated && (
                    <Badge className="bg-gradient-to-r from-violet-500/30 to-purple-500/30 text-violet-300 border border-violet-500/30 text-xs">
                      <Zap className="w-3 h-3 mr-1" />
                      AI Generated
                    </Badge>
                  )}
                  <Badge className={`text-xs ${
                    selectedLevel === '7plus' ? 'bg-pink-500/20 text-pink-400 border-pink-500/30' :
                    selectedLevel === '11plus' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                    selectedLevel === 'yr9' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                    'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                  }`}>
                    {selectedLevel === '7plus' ? '7+ Level' : 
                     selectedLevel === '11plus' ? '11+ Level' : 
                     selectedLevel === 'yr9' ? 'Year 9 Level' : 'GCSE Level'}
                  </Badge>
                </div>
                {wordOfDay && (
                  <>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2 capitalize">
                      {wordOfDay.word}
                    </h2>
                    <p className="text-gray-700 mb-3">{wordOfDay.definition}</p>
                    <p className="text-gray-600 italic text-sm">"{wordOfDay.example}"</p>
                    {wordOfDay.fun_fact && (
                      <div className="mt-4 flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-gray-600">{wordOfDay.fun_fact}</p>
                      </div>
                    )}
                    {wordOfDayMeta.theme && (
                      <p className="text-xs text-gray-500 mt-2">Today's theme: <span className="text-violet-400">{wordOfDayMeta.theme}</span></p>
                    )}
                  </>
                )}
              </div>
              
              <div className="hidden md:flex justify-end">
                <div className="w-48 h-48 rounded-full bg-gradient-to-br from-violet-500/20 to-indigo-600/20 flex items-center justify-center border border-gray-200">
                  <BookOpen className="w-20 h-20 text-violet-400/50" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Search and Filters */}
        <div className="mb-6 sm:mb-8">
          <div className="flex gap-2 sm:gap-4 mb-3 sm:mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              <Input
                placeholder="Search passages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9 sm:pl-10 bg-white/50 border-gray-200 text-gray-900 placeholder:text-gray-500 h-10 sm:h-12 text-sm"
              />
            </div>
            <Button 
              onClick={handleSearch}
              className="bg-violet-600 hover:bg-violet-700 text-gray-900 h-10 sm:h-12 px-4 sm:px-6 text-sm flex-shrink-0"
            >
              Search
            </Button>
          </div>

          {/* Filter row: scrollable filters + fixed view toggle */}
          <div className="flex items-center gap-2">
            {/* Scrollable level + type buttons */}
            <div className="flex-1 min-w-0 flex gap-2 items-center overflow-x-auto pb-1 scrollbar-hide">
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Filter className="w-3.5 h-3.5 text-gray-600" />
                <span className="text-gray-600 text-xs font-medium whitespace-nowrap">Level:</span>
              </div>
              {availableLevels.map((level) => (
                <Button
                  key={level.id}
                  variant={selectedLevel === level.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedLevel(level.id)}
                  className={`flex-shrink-0 text-xs h-7 px-2.5 ${selectedLevel === level.id 
                    ? `${level.color} text-gray-900` 
                    : "border-gray-200 text-gray-600 hover:text-gray-900 hover:border-gray-300"
                  }`}
                  data-testid={`elibrary-level-${level.id}`}
                >
                  {level.label}
                </Button>
              ))}
              
              <div className="h-5 w-px bg-white/10 flex-shrink-0" />
              
              {['all', 'fiction', 'non_fiction', 'poetry'].map((type) => (
                <Button
                  key={type}
                  variant={selectedType === type ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedType(type)}
                  className={`flex-shrink-0 text-xs h-7 px-2.5 ${selectedType === type 
                    ? "bg-indigo-600 text-gray-900" 
                    : "border-gray-200 text-gray-600 hover:text-gray-900 hover:border-gray-300"
                  }`}
                >
                  {type === 'all' ? 'All' : type === 'non_fiction' ? 'Non-Fiction' : type.charAt(0).toUpperCase() + type.slice(1)}
                </Button>
              ))}
            </div>

            {/* Fixed view toggle — never scrolls away */}
            <div className="flex gap-1 flex-shrink-0 border-l border-gray-200 pl-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('grid')}
                className={`h-7 w-7 p-0 ${viewMode === 'grid' ? 'text-gray-900' : 'text-gray-500'}`}
              >
                <Grid className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('list')}
                className={`h-7 w-7 p-0 ${viewMode === 'list' ? 'text-gray-900' : 'text-gray-500'}`}
              >
                <List className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Reading Passages - Main Content */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <BookMarked className="w-5 h-5 text-violet-400" />
                Reading Passages
              </h2>
              {selectedLevel === '11plus' && elevenPlusThemes.length > 0 ? (
                <span className="text-sm text-gray-600">
                  {Object.keys(readingHistory).length} / {
                    elevenPlusThemes.reduce((a, t) => a + (t.passages?.length || 0), 0) +
                    nonfictionThemes.reduce((a, t) => a + (t.passages?.length || 0), 0)
                  } read
                </span>
              ) : (
                <span className="text-sm text-gray-600">{passages.length} passages</span>
              )}
            </div>

            {/* 11+ Themed Passages - shown when 11plus level selected */}
            {selectedLevel === '11plus' ? (
              loadingThemes ? (
                <div className="grid gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-40 rounded-xl bg-white/50 animate-pulse" />
                  ))}
                </div>
              ) : elevenPlusThemes.length > 0 ? (
                <div className="space-y-4" data-testid="eleven-plus-themes">
                  {/* 11+ Section Header */}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-violet-600/10 to-indigo-600/10 border border-violet-500/20">
                    <BookText className="w-5 h-5 text-violet-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-medium text-sm">11+ Prep — Themed Passages</p>
                      <p className="text-gray-600 text-xs">8 fiction themes · 7 non-fiction themes · 30 passages · alphabetical order</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <div className="h-1.5 w-16 rounded-full bg-slate-700/60 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (Object.keys(readingHistory).length / 30) * 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-600">{Object.keys(readingHistory).length}/30</span>
                    </div>
                  </div>

                  {/* Alphabetical Fiction Themes */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">Fiction</p>
                  {elevenPlusThemes.map((theme, themeIdx) => {
                    const Icon = themeIcons[theme.key] || BookOpen;
                    const colors = themeColors[theme.key] || themeColors.adventure;
                    const isExpanded = expandedThemes[theme.key] !== false;
                    const readCount = (theme.passages || []).filter(p => readingHistory[p.id]).length;
                    const totalCount = theme.passages?.length || 0;
                    const themeLabel = theme.name.charAt(0).toUpperCase();

                    return (
                      <motion.div
                        key={theme.key}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: themeIdx * 0.06 }}
                        className={`rounded-xl border bg-gradient-to-br ${colors.bg} ${colors.border} overflow-hidden`}
                        data-testid={`theme-section-${theme.key}`}
                      >
                        {/* Theme Header */}
                        <button
                          onClick={() => toggleTheme(theme.key)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left"
                        >
                          <div className={`flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100/40 flex-shrink-0`}>
                            <Icon className={`w-4 h-4 ${colors.icon}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-gray-900 font-semibold text-sm">{theme.name}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${colors.badge}`}>
                                {themeLabel}
                              </span>
                            </div>
                            <p className="text-gray-600 text-xs truncate">{theme.theme_description}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-xs text-gray-600">{readCount}/{totalCount} read</span>
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-gray-600" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-600" />
                            )}
                          </div>
                        </button>

                        {/* Passages */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="grid sm:grid-cols-2 gap-3 px-4 pb-4">
                                {(theme.passages || []).map((passage) => {
                                  const isRead = !!readingHistory[passage.id];
                                  return (
                                    <div
                                      key={passage.id}
                                      onClick={() => navigate(`/elibrary/read/${passage.id}`)}
                                      className="group relative rounded-lg bg-gray-100/50 border border-white/8 hover:border-gray-300 cursor-pointer transition-all hover:bg-gray-100/70 p-4"
                                      data-testid={`passage-card-${passage.id}`}
                                    >
                                      {/* Read badge */}
                                      {isRead && (
                                        <div className="absolute top-3 right-3">
                                          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                        </div>
                                      )}
                                      <h4 className="text-gray-900 font-medium text-sm mb-1 pr-5 group-hover:text-violet-300 transition-colors leading-snug">
                                        {passage.title}
                                      </h4>
                                      <p className="text-gray-500 text-xs mb-3">{passage.author}</p>
                                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                                        <span className="flex items-center gap-1">
                                          <Clock className="w-3 h-3" />
                                          {passage.reading_time}
                                        </span>
                                        <span>{passage.word_count} words</span>
                                        {passage.comprehension_questions?.length > 0 && (
                                          <span className="flex items-center gap-1 text-amber-500">
                                            <GraduationCap className="w-3 h-3" />
                                            {passage.comprehension_questions.length} Qs
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                          isRead 
                                            ? 'bg-emerald-500/15 text-emerald-400' 
                                            : 'bg-slate-700/60 text-gray-600'
                                        }`}>
                                          {isRead ? 'Read' : 'Unread'}
                                        </span>
                                        <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-violet-400 group-hover:translate-x-0.5 transition-all" />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                  </div>

                  {/* Non-Fiction Themes */}
                  {nonfictionThemes.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1 mt-2">Non-Fiction</p>
                      {nonfictionThemes.map((theme, themeIdx) => {
                        const Icon = themeIcons[theme.key] || BookOpen;
                        const colors = themeColors[theme.key] || themeColors.conservation;
                        const isExpanded = expandedThemes[theme.key] !== false;
                        const readCount = (theme.passages || []).filter(p => readingHistory[p.id]).length;
                        const totalCount = theme.passages?.length || 0;
                        const themeLabel = theme.name.charAt(0).toUpperCase();
                        return (
                          <motion.div
                            key={theme.key}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: themeIdx * 0.06 }}
                            className={`rounded-xl border bg-gradient-to-br ${colors.bg} ${colors.border} overflow-hidden mb-3`}
                            data-testid={`nf-theme-section-${theme.key}`}
                          >
                            <button
                              onClick={() => toggleTheme(theme.key)}
                              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left"
                            >
                              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100/40 flex-shrink-0">
                                <Icon className={`w-4 h-4 ${colors.icon}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-gray-900 font-semibold text-sm">{theme.name}</span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${colors.badge}`}>{themeLabel}</span>
                                </div>
                                <p className="text-gray-600 text-xs truncate">{theme.theme_description}</p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-xs text-gray-600">{readCount}/{totalCount} read</span>
                                {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
                              </div>
                            </button>
                            <AnimatePresence>
                              {isExpanded && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <div className="grid sm:grid-cols-2 gap-3 px-4 pb-4">
                                    {(theme.passages || []).map((passage) => {
                                      const isRead = !!readingHistory[passage.id];
                                      return (
                                        <div
                                          key={passage.id}
                                          onClick={() => navigate(`/elibrary/read/${passage.id}`)}
                                          className="group relative rounded-lg bg-gray-100/50 border border-white/8 hover:border-gray-300 cursor-pointer transition-all hover:bg-gray-100/70 p-4"
                                          data-testid={`passage-card-${passage.id}`}
                                        >
                                          {isRead && (
                                            <div className="absolute top-3 right-3">
                                              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                            </div>
                                          )}
                                          <h4 className="text-gray-900 font-medium text-sm mb-1 pr-5 group-hover:text-violet-300 transition-colors leading-snug">{passage.title}</h4>
                                          <p className="text-gray-500 text-xs mb-3">{passage.author}</p>
                                          <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{passage.reading_time}</span>
                                            <span>{passage.word_count} words</span>
                                            {passage.comprehension_questions?.length > 0 && (
                                              <span className="flex items-center gap-1 text-amber-500">
                                                <GraduationCap className="w-3 h-3" />{passage.comprehension_questions.length} Qs
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center justify-between">
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${isRead ? 'bg-emerald-500/15 text-emerald-400' : 'bg-slate-700/60 text-gray-600'}`}>
                                              {isRead ? 'Read' : 'Unread'}
                                            </span>
                                            <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-violet-400 group-hover:translate-x-0.5 transition-all" />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                /* Fall back to regular passages if themes failed */
                <div className={viewMode === 'grid' ? 'grid sm:grid-cols-2 gap-4' : 'space-y-4'}>
                  <AnimatePresence>
                    {passages.map((passage, index) => (
                      <motion.div
                        key={passage.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Card 
                          className="bg-white/50 border-gray-200 hover:border-violet-500/30 transition-all cursor-pointer group overflow-hidden"
                          onClick={() => navigate(`/elibrary/read/${passage.id}`)}
                        >
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between mb-3">
                              <span className="text-2xl">{typeIcons[passage.type] || '📖'}</span>
                              <Badge className={`${difficultyColors[passage.difficulty]} border`}>
                                {passage.difficulty}
                              </Badge>
                            </div>
                            <h3 className="text-gray-900 font-semibold mb-1 group-hover:text-violet-400 transition-colors">
                              {passage.title}
                            </h3>
                            <p className="text-gray-600 text-sm mb-3">{passage.author}</p>
                            <p className="text-gray-700 text-sm line-clamp-3 mb-4">
                              {passage.excerpt?.substring(0, 150)}...
                            </p>
                            <div className="flex items-center justify-between text-xs text-gray-600">
                              <div className="flex items-center gap-3">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {passage.reading_time}
                                </span>
                                <span>{passage.word_count} words</span>
                              </div>
                              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform text-violet-400" />
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )
            ) : (
              /* Regular passage cards for other levels */
              loading ? (
                <div className="grid gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-48 rounded-xl bg-white/50 animate-pulse" />
                  ))}
                </div>
              ) : (
                <>
                  <div className={viewMode === 'grid' ? 'grid sm:grid-cols-2 gap-4' : 'space-y-4'}>
                    <AnimatePresence>
                      {passages.map((passage, index) => (
                        <motion.div
                          key={passage.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Card 
                            className="bg-white/50 border-gray-200 hover:border-violet-500/30 transition-all cursor-pointer group overflow-hidden"
                            onClick={() => navigate(`/elibrary/read/${passage.id}`)}
                            data-testid={`passage-card-${passage.id}`}
                          >
                            <CardContent className="p-5">
                              <div className="flex items-start justify-between mb-3">
                                <span className="text-2xl">{typeIcons[passage.type] || '📖'}</span>
                                <Badge className={`${difficultyColors[passage.difficulty]} border`}>
                                  {passage.difficulty}
                                </Badge>
                              </div>
                              <h3 className="text-gray-900 font-semibold mb-1 group-hover:text-violet-400 transition-colors">
                                {passage.title}
                              </h3>
                              <p className="text-gray-600 text-sm mb-3">{passage.author}</p>
                              <p className="text-gray-700 text-sm line-clamp-3 mb-4">
                                {(passage.excerpt || '').substring(0, 150)}...
                              </p>
                              <div className="flex items-center justify-between text-xs text-gray-600">
                                <div className="flex items-center gap-3">
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {passage.reading_time}
                                  </span>
                                  <span>{passage.word_count} words</span>
                                </div>
                                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform text-violet-400" />
                              </div>
                              {passage.themes && (
                                <div className="flex flex-wrap gap-1 mt-3">
                                  {passage.themes.slice(0, 3).map((theme) => (
                                    <span key={theme} className="px-2 py-0.5 rounded-full bg-slate-700/50 text-gray-600 text-xs">
                                      {theme}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                  {passages.length === 0 && (
                    <div className="text-center py-12">
                      <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-gray-600">No passages found. Try a different search or filter.</p>
                    </div>
                  )}
                </>
              )
            )}
          </div>

          {/* Sidebar - Vocabulary & Quick Actions */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="bg-gradient-to-br from-violet-600/10 to-indigo-600/10 border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-gray-900 text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-400" />
                  Quick Start
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  className="w-full justify-start bg-white/50 hover:bg-slate-700/50 text-gray-900"
                  onClick={() => navigate('/elibrary/vocabulary')}
                >
                  <BookOpen className="w-4 h-4 mr-2 text-emerald-400" />
                  Vocabulary Builder
                </Button>
                <Button 
                  className="w-full justify-start bg-gradient-to-r from-violet-600/20 to-purple-600/20 hover:from-violet-600/30 hover:to-purple-600/30 text-violet-300 border border-violet-500/30"
                  onClick={() => setActiveMainTab('my-passages')}
                >
                  <Library className="w-4 h-4 mr-2 text-violet-400" />
                  My Saved Passages
                  {savedPassages.length > 0 && (
                    <Badge className="ml-auto bg-violet-500/30 text-violet-300 text-xs">{savedPassages.length}</Badge>
                  )}
                </Button>
                <Button 
                  className="w-full justify-start bg-gradient-to-r from-amber-600/20 to-orange-600/20 hover:from-amber-600/30 hover:to-orange-600/30 text-amber-300 border border-amber-500/30"
                  onClick={() => setShowPassageGenerator(true)}
                >
                  <Wand2 className="w-4 h-4 mr-2 text-amber-400" />
                  AI Passage Generator
                  <Sparkles className="w-3 h-3 ml-auto text-amber-400" />
                </Button>
                <Button 
                  className="w-full justify-start bg-gradient-to-r from-cyan-500/20 to-blue-500/20 hover:from-cyan-500/30 hover:to-blue-500/30 text-cyan-300 border border-cyan-500/30"
                  onClick={() => navigate('/reading-challenge/11-plus')}
                >
                  <TrendingUp className="w-4 h-4 mr-2 text-cyan-400" />
                  Reading Challenge
                  <ChevronRight className="ml-auto w-4 h-4" />
                </Button>
              </CardContent>
            </Card>

            {/* Vocabulary Preview */}
            <Card className="bg-white/50 border-gray-200">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-gray-900 text-lg flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-emerald-400" />
                    Vocabulary
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/elibrary/vocabulary')}
                    className="text-violet-400 hover:text-violet-300"
                  >
                    See All
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {vocabulary.map((word) => (
                  <div 
                    key={word.word}
                    className="p-3 rounded-lg bg-slate-700/30 hover:bg-slate-700/50 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/elibrary/vocabulary?word=${word.word}`)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900 group-hover:text-violet-400 transition-colors">
                        {word.word}
                      </span>
                      <Badge className={`${difficultyColors[word.difficulty]} border text-xs`}>
                        {word.difficulty}
                      </Badge>
                    </div>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {word.definition}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Exam Mode Quick Access */}
            <Card className="bg-white/50 border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-gray-900 text-lg flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400" />
                  Exam Prep Modes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  { name: '7+ Reading', desc: 'Comprehension for younger learners', color: 'text-cyan-400', link: '/reading-challenge/7-plus' },
                  { name: '11+ Reading', desc: 'Advanced comprehension practice', color: 'text-emerald-400', link: '/reading-challenge/11-plus' },
                  { name: 'Timed Practice', desc: '25 min challenges (coming soon)', color: 'text-rose-400', link: null }
                ].map((mode) => (
                  <div 
                    key={mode.name}
                    className={`flex items-center justify-between p-3 rounded-lg transition-colors ${mode.link ? 'bg-slate-700/30 hover:bg-slate-700/50 cursor-pointer' : 'bg-slate-700/20 cursor-not-allowed opacity-60'}`}
                    onClick={() => mode.link && navigate(mode.link)}
                  >
                    <div>
                      <p className={`font-medium ${mode.color}`}>{mode.name}</p>
                      <p className="text-gray-600 text-xs">{mode.desc}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* AI Passage Generator Modal */}
        <Dialog open={showPassageGenerator} onOpenChange={setShowPassageGenerator}>
          <DialogContent className="sm:max-w-[600px] bg-gray-100 border-gray-200 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900 flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-amber-400" />
                AI Passage Generator
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Generate a custom reading passage on any topic for your {
                  selectedLevel === '7plus' ? '7+' : 
                  selectedLevel === '11plus' ? '11+' : 
                  selectedLevel === 'yr9' ? 'Year 9' : 'GCSE'
                } level
              </DialogDescription>
            </DialogHeader>

            {!generatedPassage ? (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="topic" className="text-gray-700">Topic or Theme</Label>
                  <Input
                    id="topic"
                    placeholder="e.g., Space exploration, Ancient Egypt, Friendship..."
                    value={generatorForm.topic}
                    onChange={(e) => setGeneratorForm({...generatorForm, topic: e.target.value})}
                    className="bg-white/50 border-gray-200 text-gray-900"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Passage Type</Label>
                    <Select 
                      value={generatorForm.type} 
                      onValueChange={(value) => setGeneratorForm({...generatorForm, type: value})}
                    >
                      <SelectTrigger className="bg-white/50 border-gray-200 text-gray-900">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="fiction" className="text-gray-900">Fiction (Story)</SelectItem>
                        <SelectItem value="non_fiction" className="text-gray-900">Non-Fiction (Article)</SelectItem>
                        <SelectItem value="poetry" className="text-gray-900">Poetry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-gray-700">Approximate Length</Label>
                    <Select 
                      value={String(generatorForm.wordCount)} 
                      onValueChange={(value) => setGeneratorForm({...generatorForm, wordCount: parseInt(value)})}
                    >
                      <SelectTrigger className="bg-white/50 border-gray-200 text-gray-900">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200">
                        <SelectItem value="150" className="text-gray-900">Short (~150 words)</SelectItem>
                        <SelectItem value="200" className="text-gray-900">Medium (~200 words)</SelectItem>
                        <SelectItem value="300" className="text-gray-900">Long (~300 words)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  onClick={generatePassage}
                  disabled={!generatorForm.topic.trim() || generatingPassage}
                  className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-gray-900"
                >
                  {generatingPassage ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating your passage...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Passage
                    </>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div className="flex items-center justify-between">
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                    <Sparkles className="w-3 h-3 mr-1" />
                    AI Generated
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setGeneratedPassage(null)}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    Generate Another
                  </Button>
                </div>

                <Card className="bg-white/50 border-gray-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-gray-900 text-lg">{generatedPassage.title}</CardTitle>
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <span>{generatedPassage.word_count} words</span>
                      <span>•</span>
                      <span>{generatedPassage.reading_time}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm prose-invert max-w-none max-h-48 overflow-y-auto">
                      <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {generatedPassage.excerpt}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Comprehension Questions Preview */}
                {generatedPassage.comprehension_questions?.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-gray-900 font-medium flex items-center gap-2">
                        <GraduationCap className="w-4 h-4 text-amber-400" />
                        Comprehension Questions ({generatedPassage.comprehension_questions.length})
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {generatedPassage.comprehension_questions.slice(0, 2).map((q, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-white/30 border border-gray-200">
                          <p className="text-gray-700 text-sm">{idx + 1}. {q.question}</p>
                        </div>
                      ))}
                      {generatedPassage.comprehension_questions.length > 2 && (
                        <p className="text-gray-500 text-xs text-center">
                          +{generatedPassage.comprehension_questions.length - 2} more questions available
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  <Button 
                    onClick={() => {
                      // Store passage in sessionStorage for reading page
                      sessionStorage.setItem('ai_generated_passage', JSON.stringify(generatedPassage));
                      setShowPassageGenerator(false);
                      navigate('/elibrary/read/ai-generated');
                    }}
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-gray-900"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Read & Practice with AI Tools
                  </Button>
                  <Button 
                    onClick={savePassageToLibrary}
                    disabled={savingPassage}
                    className="w-full bg-gradient-to-r from-emerald-600/80 to-teal-600/80 hover:from-emerald-500 hover:to-teal-500 text-gray-900"
                  >
                    {savingPassage ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save to My Library
                  </Button>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-2">
                    <Button 
                      variant="outline"
                      onClick={() => setGeneratedPassage(null)}
                      className="border-gray-300 text-gray-700 hover:bg-white"
                    >
                      Generate Another
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setShowPassageGenerator(false);
                        setGeneratedPassage(null);
                        setGeneratorForm({ topic: '', type: 'fiction', wordCount: 200 });
                      }}
                      className="border-gray-300 text-gray-700 hover:bg-white"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* My Saved Passages Section - Shown when activeMainTab is 'my-passages' */}
        {activeMainTab === 'my-passages' && (
          <Dialog open={true} onOpenChange={() => setActiveMainTab('browse')}>
            <DialogContent className="sm:max-w-[700px] bg-gray-100 border-gray-200 max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900 flex items-center gap-2">
                  <Library className="w-5 h-5 text-violet-400" />
                  My Saved Passages
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Your personal library of AI-generated passages ({savedPassages.length} saved)
                </DialogDescription>
              </DialogHeader>

              {loadingSaved ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
                </div>
              ) : savedPassages.length === 0 ? (
                <div className="text-center py-12">
                  <Library className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-gray-900 font-medium mb-2">No saved passages yet</h3>
                  <p className="text-gray-600 text-sm mb-4">Generate a passage and save it to your library to revisit later</p>
                  <Button 
                    onClick={() => { setActiveMainTab('browse'); setShowPassageGenerator(true); }}
                    className="bg-amber-600 hover:bg-amber-500 text-gray-900"
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate Your First Passage
                  </Button>
                </div>
              ) : (
                <SavedPassagesAccordion 
                  passages={savedPassages}
                  onRead={(passage) => { setActiveMainTab('browse'); openSavedPassage(passage); }}
                  onDelete={deleteSavedPassage}
                />
              )}
            </DialogContent>
          </Dialog>
        )}
      </main>
    </div>
  );
};

export default ELibrary;
